import {
  DedicatedGroup,
  FrontendRegistrationGroup,
  RegistrationMode,
} from "@timeedit/registration-schemas";
import {
  exhaustiveMatchingGuard,
  isDefined,
} from "@timeedit/registration-shared";
import { AllocationControlsProps } from "../../../AllocationControls";

/**
 *
 * @param groups Groups to check
 * @returns If all groups are open it is true, if just one is not open it is false
 */
export function isOpen(groups: FrontendRegistrationGroup[]): boolean {
  if (groups.length === 0) return false;
  return groups.every((group) => group.open ?? false);
}

type IsFullProps = {
  groups: FrontendRegistrationGroup[];
  mode: RegistrationMode;
};
/**
 *
 * @param mode Which mode the user is in
 * @param groups Groups to check
 * @returns If any group is full the function returns true, otherwise false
 */
export function isFull({ mode, groups }: IsFullProps): boolean {
  return groups.some((group) =>
    mode === "student"
      ? group.seats.available === 0
      : group.seats.raw.total - group.seats.raw.taken <= 0
  );
}

/**
 *
 * @param groups Groups to check
 * @returns Combined allocation status of groups.
 * Return following in priority order:
 * 1: If any group is not allocated, the status is "NOT_ALLOCATED".
 * 2: If any group has multiple allocations the status is "MULTIPLE_ALLOCATIONS".
 * 3: If any group is allocated to another group the status is "ALLOCATED_TO_OTHER".
 * 4: If all groups are allocated the status is "ALLOCATED_TO_THIS".
 */
export function getAllocationStatus(
  groups: FrontendRegistrationGroup[]
): AllocationControlsProps["allocationStatus"] {
  return groups.reduce(
    (
      allocationStatus: AllocationControlsProps["allocationStatus"],
      group,
      index
    ) => {
      if (index === 0) return group.allocationStatus;

      if (
        group.allocationStatus === "NOT_ALLOCATED" ||
        allocationStatus === "NOT_ALLOCATED"
      )
        return "NOT_ALLOCATED";

      if (
        group.allocationStatus === "MULTIPLE_ALLOCATIONS" ||
        allocationStatus === "MULTIPLE_ALLOCATIONS"
      )
        return "MULTIPLE_ALLOCATIONS";

      if (
        group.allocationStatus === "ALLOCATED_TO_OTHER" ||
        allocationStatus === "ALLOCATED_TO_OTHER"
      )
        return "ALLOCATED_TO_OTHER";

      return "ALLOCATED_TO_THIS";
    },
    "NOT_ALLOCATED"
  );
}

type DedicationData = {
  relation: Extract<DedicatedGroup, { kind: "relation" }>["data"];
  category: Extract<DedicatedGroup, { kind: "category" }>["data"];
};

/**
 *
 * @param dedicated Dedicated group information
 * @returns Extracts the dedication data and accumulate it to relation and category array
 */
export function getDedicationData(dedicated: DedicatedGroup[]): DedicationData {
  return dedicated.reduce<DedicationData>(
    (dedicationData: DedicationData, { kind, data }) => {
      switch (kind) {
        case "category": {
          const category = dedicationData.category;
          if (isDefined(category)) {
            return { ...dedicationData, category: [...category, ...data] };
          }
          return dedicationData;
        }
        case "relation": {
          const relation = dedicationData.relation;
          if (isDefined(relation)) {
            return { ...dedicationData, relation: [...relation, ...data] };
          }
          return dedicationData;
        }
        case "none": {
          return dedicationData;
        }
        default: {
          return exhaustiveMatchingGuard(kind);
        }
      }
    },
    { category: [], relation: [] }
  );
}
